import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PhoneCall, ArrowLeft, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-paper px-6">
            <div className="w-full max-w-md">
                <div className="mb-8 flex">
                    <img src="/inspiro-connect-logo.png" alt="Inspiro Connect" className="h-9 w-auto object-contain" />
                </div>

                {sent ? (
                    <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center" data-testid="forgot-success">
                        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-100">
                            <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                        </div>
                        <h1 className="mt-4 font-heading text-2xl font-bold">Check your email</h1>
                        <p className="mt-2 text-sm text-stone2">
                            If an account exists for <span className="font-semibold text-ink">{email}</span>, we've sent a password reset link. It expires in 1 hour.
                        </p>
                        <Link to="/login" data-testid="back-to-login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:underline">
                            <ArrowLeft size={16} /> Back to sign in
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Password recovery</div>
                        <h1 className="mt-3 font-heading text-4xl font-bold leading-tight tracking-tight">Forgot your <span className="text-brand">password?</span></h1>
                        <p className="mt-3 text-sm text-stone2">Enter your account email and we'll send you a secure link to reset it.</p>

                        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-stone2">Email</label>
                                <div className="relative mt-2">
                                    <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                    <input
                                        data-testid="forgot-email-input"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full rounded-lg border border-stone-300 bg-white py-3 pl-10 pr-4 text-base outline-none transition-all focus:border-ink focus:ring-2 focus:ring-brand/30"
                                        placeholder="you@company.com"
                                    />
                                </div>
                            </div>
                            {err && <div data-testid="forgot-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
                            <button
                                data-testid="forgot-submit-button"
                                type="submit"
                                disabled={busy}
                                className="w-full rounded-full bg-ink py-3.5 font-heading text-base font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-stone-800 disabled:opacity-50"
                            >
                                {busy ? "Sending…" : "Send reset link"}
                            </button>
                        </form>

                        <Link to="/login" data-testid="back-to-login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink">
                            <ArrowLeft size={16} /> Back to sign in
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
